/**
 * The template editor — the first working slice of the WYSIWYG builder (R2, D21).
 *
 * Three panes: the block OUTLINE, a live PREVIEW, and the selected block's PROPERTIES.
 *
 * ── The preview is the real thing ──
 *
 * It resolves the template against sample data with the SAME `resolveTemplate` the render worker
 * uses and mounts the SAME `<ReportCanvas>` the report builder does — which mounts the same
 * `<ReportDocument>` the PDF is serialised from. So what you edit is what a client gets, not an
 * approximation that drifts on the first template change.
 *
 * ── Editing structure, not figures ──
 *
 * The report builder edits a generated report's VALUES (overrides). This edits the TEMPLATE: the
 * blocks, their props and bindings. Selecting a block in the outline shows its config; changing
 * it re-resolves and the preview updates live. Saving writes the draft back to the store.
 *
 * ── Published is immutable ──
 *
 * A published version cannot be changed (R4 — a report reproduces the version it was rendered
 * against). The store refuses it with 409; here the editor goes read-only and says so. Editing a
 * published template is a new version, which this first slice does not yet create — it surfaces
 * the limit rather than pretending past it.
 */
import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ApiError, templates } from '../lib/api';
import { ReportCanvas } from '../components/ReportCanvas';
import { resolveTemplate, type ResolvedDocument } from '@pdmg/reporting-bindings';
import {
  BLOCK_REGISTRY, SAMPLE_REPORT, SAMPLE_EMAIL_REPORT, SAMPLE_AUDIO_REPORT, SAMPLE_DOOH_REPORT,
  chainFromPath, blockAt, deleteBlock, moveBlock, insertBlockAfter, appendBlock, makeBlock,
  collectIds, isContainer, propFields, coerceProp, type PropField,
} from '@pdmg/reporting-schema';

type Template = Record<string, any>;

/** Sample data to preview against, chosen by the template's first channel. */
function sampleFor(channels: string[]): Record<string, unknown> {
  const c = (channels[0] ?? 'DISPLAY').toUpperCase();
  if (c === 'EMAIL') return SAMPLE_EMAIL_REPORT as Record<string, unknown>;
  if (c === 'AUDIO') return SAMPLE_AUDIO_REPORT as Record<string, unknown>;
  if (c === 'DOOH') return SAMPLE_DOOH_REPORT as Record<string, unknown>;
  return SAMPLE_REPORT as Record<string, unknown>;
}

/** One row in the block outline — carries a PATH so a click maps to a spot in the template. */
interface OutlineRow { path: string; label: string; type: string; depth: number; kind: 'page' | 'block'; }

function buildOutline(tpl: Template): OutlineRow[] {
  const rows: OutlineRow[] = [];
  const walkBlocks = (blocks: any[], base: string, depth: number) => {
    (blocks ?? []).forEach((b, i) => {
      const path = `${base}.${i}`;
      rows.push({ path, label: b.id ?? b.type, type: b.type, depth, kind: 'block' });
      if (Array.isArray(b.children)) walkBlocks(b.children, `${path}.children`, depth + 1);
    });
  };
  (tpl.pages ?? []).forEach((p: any, pi: number) => {
    rows.push({ path: `pages.${pi}`, label: p.title ?? p.id, type: `page (${p.mode})`, depth: 0, kind: 'page' });
    walkBlocks(p.blocks, `pages.${pi}.blocks`, 1);
  });
  return rows;
}

/** Read a value at a dotted path — the outline paths address into the template. */
function at(obj: any, path: string): any {
  return path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
}

/** Immutably set a value at a dotted path, returning a new template. */
function setAt(obj: any, path: string, value: any): any {
  const keys = path.split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

export function TemplateEditorScreen() {
  const { id = '', version = '' } = useParams();
  const [tpl, setTpl] = React.useState<Template | null>(null);
  const [doc, setDoc] = React.useState<ResolvedDocument | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [rawMode, setRawMode] = React.useState(false);
  const [rawText, setRawText] = React.useState('');
  const [propsError, setPropsError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string>('');
  const [dirty, setDirty] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Load the template once.
  React.useEffect(() => {
    templates.get(id, Number(version))
      .then((t) => setTpl(t as Template))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [id, version]);

  // Re-resolve the preview whenever the template changes.
  React.useEffect(() => {
    if (!tpl) return;
    let live = true;
    const data = { lifecycle: (tpl.lifecycle?.[0] ?? tpl.pages?.[0]?.lifecycle?.[0] ?? 'POSTFLIGHT'),
      ...sampleFor(tpl.channels ?? ['DISPLAY']) };
    resolveTemplate(tpl as never, data as never, BLOCK_REGISTRY as never)
      .then((d) => { if (live) { setDoc(d); setPreviewError(null); } })
      // A broken block must not blank the editor: keep the last good preview, show the reason,
      // and leave the outline usable so the offending block can be fixed or deleted.
      .catch((e) => { if (live) setPreviewError(String(e?.message ?? e)); });
    return () => { live = false; };
  }, [tpl]);

  const published = tpl?.status === 'published';

  function selectBlock(path: string) {
    if (!tpl) return;
    setSelected(path);
    setConfirmDelete(false);
    setRawMode(false);
    setPropsError(null);
    const block = at(tpl, path);
    setRawText(JSON.stringify(block?.props ?? {}, null, 2));
  }

  /** Set one prop by name, coerced to its declared type. */
  function setProp(field: PropField, raw: string | boolean) {
    if (!tpl || !selected) return;
    let value: unknown;
    try { value = coerceProp(field.type, raw); }
    catch (e) { setPropsError(`${field.name}: ${String((e as Error).message)}`); return; }
    setPropsError(null);
    const block = at(tpl, selected);
    const nextProps = { ...(block?.props ?? {}) } as Record<string, unknown>;
    // undefined means "unset" — remove the key rather than storing a null.
    if (value === undefined) delete nextProps[field.name];
    else nextProps[field.name] = value;
    mutate(setAt(tpl, `${selected}.props`, nextProps));
  }

  /** The raw-JSON escape hatch, for props the form cannot express. */
  function applyRaw() {
    if (!tpl || !selected) return;
    let parsed: unknown;
    try { parsed = JSON.parse(rawText); }
    catch (e) { setPropsError(`not valid JSON: ${String((e as Error).message)}`); return; }
    setPropsError(null);
    mutate(setAt(tpl, `${selected}.props`, parsed));
  }

  function mutate(next: Record<string, any>) { setTpl(next); setDirty(true); }

  function onDelete() {
    if (!tpl || !selected) return;
    mutate(deleteBlock(tpl, chainFromPath(selected)));
    setSelected(null);
    setConfirmDelete(false);
  }
  function onMove(delta: number) {
    if (!tpl || !selected) return;
    mutate(moveBlock(tpl, chainFromPath(selected), delta));
  }
  function onAdd(type: string) {
    if (!tpl) return;
    const manifest = BLOCK_REGISTRY.find((m) => m.type === type);
    if (!manifest) return;
    const block = makeBlock(manifest as never, collectIds(tpl));
    // Into a selected container's children, else after the selected block, else onto page 1.
    const chain = selected ? chainFromPath(selected) : [0];
    const selManifest = selected
      ? BLOCK_REGISTRY.find((m) => m.type === blockAt(tpl, chain)?.type)
      : undefined;
    const next = !selected
      ? appendBlock(tpl, [0], block as never)
      : isContainer(selManifest as never)
        ? appendBlock(tpl, chain, block as never)
        : insertBlockAfter(tpl, chain, block as never);
    mutate(next);
    setAdding(false);
  }

  async function save() {
    if (!tpl) return;
    setStatus('Saving…');
    try {
      const r = await templates.save(id, Number(version), tpl);
      setStatus(r.written ? 'Saved.' : 'No change.');
      setDirty(false);
    } catch (e) {
      setStatus(e instanceof ApiError ? e.message : String(e));
    }
  }

  if (error) return <div className="banner error">{error}</div>;
  if (!tpl) return <div className="busy">Loading template…</div>;

  const outline = buildOutline(tpl);
  const selBlock = selected ? at(tpl, selected) : null;

  return (
    <div className="tpl-editor">
      <div className="tpl-bar">
        <div>
          <Link to="/templates">Templates</Link> <span>/</span>{' '}
          <strong>{tpl.name}</strong> <code>v{tpl.version}</code>{' '}
          <span className={`pill ${published ? 'ok' : ''}`}>{tpl.status}</span>
        </div>
        <div className="tpl-bar-actions">
          {!published && (
            <div className="tpl-add">
              <button className="button" onClick={() => setAdding((a) => !a)}>+ Add block</button>
              {adding && (
                <div className="tpl-add-menu">
                  <div className="tpl-add-hint">
                    {selected ? 'Insert after / into the selected block' : 'Append to the first page'}
                  </div>
                  {BLOCK_REGISTRY.map((m) => (
                    <button key={m.type} className="tpl-add-item" onClick={() => onAdd(m.type)}>
                      <span>{m.label}</span>
                      <span className="muted">{m.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {published
            ? <span className="muted">Published — immutable. Editing forks a new version (coming next).</span>
            : <button className="button primary" onClick={save} disabled={!dirty}>
                {dirty ? 'Save draft' : 'Saved'}
              </button>}
          {status && <span className="muted" style={{ marginLeft: 10 }}>{status}</span>}
        </div>
      </div>

      <div className="tpl-panes">
        {/* Outline */}
        <aside className="tpl-outline">
          <h3>Structure</h3>
          {outline.map((r) => (
            <button
              key={r.path}
              className={`tpl-node depth-${Math.min(r.depth, 4)} ${r.kind}${selected === r.path ? ' on' : ''}`}
              onClick={() => r.kind === 'block' && selectBlock(r.path)}
              disabled={r.kind === 'page'}
              title={r.path}
            >
              <span className="tpl-node-label">{r.label}</span>
              <span className="tpl-node-type">{r.type}</span>
            </button>
          ))}
        </aside>

        {/* Live preview — the same components as the PDF */}
        <div className="tpl-preview">
          {previewError && (
            <div className="banner error tpl-preview-error">
              This template does not render yet: {previewError}
            </div>
          )}
          {doc
            ? <ReportCanvas doc={doc} changed={EMPTY} editing={false} onPick={NOOP} zoom={0.8} />
            : !previewError && <div className="busy">Rendering preview…</div>}
        </div>

        {/* Properties */}
        <aside className="tpl-props">
          <h3>Properties</h3>
          {!selBlock ? (
            <p className="muted">Select a block in the structure to edit it.</p>
          ) : (
            <>
              <dl className="facts">
                <div><dt>Block</dt><dd><code>{selBlock.id}</code></dd></div>
                <div><dt>Type</dt><dd>{selBlock.type}</dd></div>
              </dl>
              {!published && (
                <div className="tpl-block-actions">
                  <button className="button" onClick={() => onMove(-1)} title="Move up">↑</button>
                  <button className="button" onClick={() => onMove(1)} title="Move down">↓</button>
                  {confirmDelete ? (
                    <>
                      <button className="button danger" onClick={onDelete}>Confirm delete</button>
                      <button className="button" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    </>
                  ) : (
                    <button className="button danger" onClick={() => setConfirmDelete(true)}
                            title="Delete block">Delete</button>
                  )}
                </div>
              )}
              <div className="tpl-props-head">
                <label className="tpl-props-label">properties</label>
                <button className="tpl-link" onClick={() => setRawMode((m) => !m)}>
                  {rawMode ? 'form' : 'edit as JSON'}
                </button>
              </div>
              {propsError && <div className="banner error">{propsError}</div>}
              {rawMode ? (
                <>
                  <textarea
                    className="tpl-props-json" value={rawText} spellCheck={false}
                    disabled={published} onChange={(e) => setRawText(e.target.value)}
                  />
                  {!published && <button className="button" onClick={applyRaw}>Apply</button>}
                </>
              ) : (
                <PropForm
                  fields={propFields(
                    BLOCK_REGISTRY.find((m) => m.type === selBlock.type) as never,
                    selBlock.props ?? {})}
                  disabled={published}
                  onChange={setProp}
                />
              )}
              {Object.keys(selBlock.bindings ?? {}).length > 0 && (
                <>
                  <label className="tpl-props-label">bindings (read-only)</label>
                  <pre className="tpl-bindings">{JSON.stringify(selBlock.bindings, null, 2)}</pre>
                </>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * A block's props as a typed form, one control per manifest field.
 *
 * The control matches the declared type: checkbox, select for an enum, number, text. A
 * non-scalar value gets a small JSON field. An undeclared prop is shown but marked, so a stray
 * prop is visible rather than silently kept. Each edit is coerced and applied on change, so the
 * live preview reflects it immediately.
 */
function PropForm({ fields, disabled, onChange }: {
  fields: PropField[];
  disabled: boolean;
  onChange: (field: PropField, raw: string | boolean) => void;
}) {
  if (fields.length === 0) return <p className="muted">This block takes no properties.</p>;
  return (
    <div className="tpl-form">
      {fields.map((f) => (
        <div className={`tpl-field${f.declared ? '' : ' undeclared'}`} key={f.name}>
          <label className="tpl-field-label">
            {f.name}{f.required && <span className="tpl-req" title="required">*</span>}
            {!f.declared && <span className="tpl-badge" title="not in this block's manifest">extra</span>}
          </label>
          {f.type === 'boolean' ? (
            <input type="checkbox" checked={!!f.value} disabled={disabled}
                   onChange={(e) => onChange(f, e.target.checked)} />
          ) : f.type === 'enum' ? (
            <select value={String(f.value ?? '')} disabled={disabled}
                    onChange={(e) => onChange(f, e.target.value)}>
              {(f.values ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : f.type === 'number' ? (
            <input type="number" value={f.value === undefined ? '' : String(f.value)} disabled={disabled}
                   onChange={(e) => onChange(f, e.target.value)} />
          ) : f.type === 'json' ? (
            <textarea className="tpl-field-json" spellCheck={false} disabled={disabled}
                      defaultValue={JSON.stringify(f.value)}
                      onBlur={(e) => onChange(f, e.target.value)} />
          ) : (
            <input type={f.type === 'color' ? 'color' : 'text'}
                   value={String(f.value ?? '')} disabled={disabled}
                   onChange={(e) => onChange(f, e.target.value)} />
          )}
          {f.description && <div className="tpl-field-desc">{f.description}</div>}
        </div>
      ))}
    </div>
  );
}

const EMPTY: ReadonlySet<string> = new Set();
const NOOP = () => {};
